/**
 * Copyright (c) 2023, CYBRA Corp. All rights reserved.
 */
import { DraggableDirective } from "../../directives/draggable.directive";
import { Component, OnInit, ViewChild, EventEmitter, Input, OnDestroy } from '@angular/core';
import { Subscription } from "rxjs";
import { ObjectInstance } from "../../models/object-instance";
import { MainDisplayParameter } from "./main-display-parameter";
import { ImageService } from "app/services/image.service";
import { ObjectService } from "../../services/object.service";
import { DisplayModule } from "../../shared/display.module";

declare var $: any;


@Component({
  selector: 'app-asset-locate-popup',
  templateUrl: './asset-popup-dialog.component.html'
})
export class AssetPopupDialogComponent implements OnInit, OnDestroy {

  //Emitter used to hide the popup from the Parent.  Needs to be done inside here so that the drag directive can be reset
  @Input()
  callHideEmitter: EventEmitter<boolean>;

  //Asset whose information is being displayed in the popup
  @Input()
  asset: ObjectInstance;

  mainDisplayParameters: MainDisplayParameter[];

  //Unique ID to give the popup.  Parent references it when showing the dialog
  @Input()
  dialogId: string;

  @Input()
  popupInModal = false;

  //Draggable Directive added to that the popup is draggable.  Referenced so that it can be reset when the popup is hidden
  @ViewChild(DraggableDirective) mDraggableDirective;

  //Subscription to the "hide" events from the Parent
  mHideSubscription: Subscription;

  objectReferenceName: { [key: number]: String } = [];

  displayFullDetailsFlag: boolean = false;


  constructor(
    private mImageService: ImageService,
    private mObjectService: ObjectService
  ) { }


  async ngOnInit(): Promise<void> {
    if (this.callHideEmitter != null) {
      this.mHideSubscription = this.callHideEmitter.subscribe(value => {
        this.hide();
      });
    }

    // Load all the Object Instances
    //const objects = await this.mObjectService.getAllObjectInstances();
    //for(const object of objects) {
    //  this.objectReferenceName[object.id] = object.name;
    //}

    this.mainDisplayParameters = MainDisplayParameter.getDisplayParameters(this.asset);
  }

  ngOnDestroy(): void {
    if (this.mHideSubscription != null) {
      this.mHideSubscription.unsubscribe();
      this.mHideSubscription = null;
    }
  }


  public async show(): Promise<void> {
    console.log("Show object instance " + this.asset.id);
    $("#" + this.dialogId).show();
  }

  public hide(): void {
    this.hideFullDetails();
    $("#" + this.dialogId).hide();
    this.mDraggableDirective.reset();
  }


  getIdForDialogHeader(): string {
    return this.dialogId + "_header";
  }

  getPathForAssetImage(): string {
    return this.mImageService.getImageSourceForObjectInstance(this.asset);
  }

  getPathForAssetIcon(): string {
    return this.mImageService.getIconSourceForObjectInstance(this.asset);
  }

  /**
   * Returns the Display String for the given measurement
   */
  getDisplayForMeasurement(valueInMeters: number): string {
    return DisplayModule.getDisplayForMeasurement(valueInMeters);
  }

  /**
   * Returns a boolean indicating if the Asset Image is present.
   * Used in the HTML to display the Image or not. 
   */
  public isAssetImagePresent(): boolean {
    if (this.asset?.image != null) {
      return true;
    }
    return false;
  }

  /**
   * Returns a boolean indicating if the "Show Full Details" / "Hide Full Details"
   * Link / Panels need to be shown.  This returns true if the corresponding Asset
   * has Data for a Property that is not a "Main Display" property.
   */
  public isShowFullDetailsNeeded(): boolean {
    if (this.asset?.data?.length > 0) {
      for (let datum of this.asset.data) {
        if (!datum.property?.mainDisplay) {
          return true;
        }
      }
    }
    return false;
  }

  /**
   * Used to show the Full Details of the Asset.
   * This includes the data for the properties that are not marked "main display".
   */
  showFullDetails(): void {
    this.displayFullDetailsFlag = true;
  }

  /**
   * Used to hide the Full Details of the Asset.
   */
  hideFullDetails(): void {
    this.displayFullDetailsFlag = false;
  }

}
